import type { ReactNode } from "react"

import { Button, EmptyState, cn } from "@ibada/ui"

import { useWidgetRefreshing } from "./widget-chrome-context"

export interface WidgetBoundaryProps<T> {
  /** No usable data yet (initial load) => render the skeleton. */
  isLoading: boolean
  /** Request failed. Combined with no data => render the error state. */
  isError: boolean
  /** A request is in flight. Combined with existing data => subtle refreshing (keep content, header spinner). */
  isFetching?: boolean
  data: T | undefined
  /** Treats otherwise-loaded data as empty (e.g. zero rows) => render the empty state. */
  isEmpty?: (data: T) => boolean
  skeleton: ReactNode
  emptyTitle?: string
  emptyDescription?: string
  emptyIcon?: ReactNode
  emptyAction?: ReactNode
  errorTitle?: string
  errorDescription?: string
  /** When provided, the error state shows a Retry button that calls this. */
  onRetry?: () => void
  retryLabel?: string
  children: (data: T) => ReactNode
  className?: string
}

/**
 * The per-widget data boundary. It renders exactly one of: skeleton (initial load), local error (with an
 * optional Retry that refetches only this widget), empty state, or the loaded content — never coupling to
 * any other widget. A background refetch keeps the loaded content on screen and only raises the shell's
 * subtle "refreshing" spinner (via {@link useWidgetRefreshing}), so refresh never flashes the skeleton.
 */
export function WidgetBoundary<T>({
  isLoading,
  isError,
  isFetching,
  data,
  isEmpty,
  skeleton,
  emptyTitle = "Nothing to show yet",
  emptyDescription,
  emptyIcon,
  emptyAction,
  errorTitle = "Unable to load",
  errorDescription,
  onRetry,
  retryLabel = "Retry",
  children,
  className,
}: WidgetBoundaryProps<T>) {
  const hasData = data !== undefined
  useWidgetRefreshing(Boolean(isFetching && !isLoading && hasData))

  if (isLoading && !hasData) {
    return <div className={className}>{skeleton}</div>
  }

  if (isError && !hasData) {
    return (
      <div className={cn("py-1", className)}>
        <EmptyState
          surface={false}
          compact
          tone="danger"
          title={errorTitle}
          description={errorDescription}
          action={
            onRetry ? (
              <Button size="sm" variant="outline" onClick={onRetry}>
                {retryLabel}
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  if (hasData && isEmpty?.(data as T)) {
    return (
      <div className={cn("py-1", className)}>
        <EmptyState
          surface={false}
          compact
          title={emptyTitle}
          description={emptyDescription}
          icon={emptyIcon}
          action={emptyAction}
        />
      </div>
    )
  }

  if (hasData) {
    return <div className={className}>{children(data as T)}</div>
  }

  return <div className={className}>{skeleton}</div>
}
